// src/components/forum/NewPostEditor.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { apiClient } from '../../api/apiClient';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PrimaryButton } from '../../components/ui';
import { color, font } from '../../theme/tokens';

// --- 样式 ---
const ModalBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(7, 2, 5, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
    z-index: 1000;
`;

const ModalContent = styled.div`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    height: min(90vh, 900px);
    max-width: 1200px;
    background-color: ${color.surface};
    border: 1px solid ${color.line};
    color: ${color.text};
    display: flex;
    flex-direction: column;
    font-family: ${font.body};

    /* corner ticks */
    &::before, &::after {
        content: '';
        position: absolute;
        width: 14px;
        height: 14px;
        pointer-events: none;
        z-index: 2;
    }
    &::before { left: -1px; top: -1px; border-left: 1px solid ${color.blood}; border-top: 1px solid ${color.blood}; }
    &::after { right: -1px; bottom: -1px; border-right: 1px solid ${color.blood}; border-bottom: 1px solid ${color.blood}; }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 18px 24px;
    border-bottom: 1px solid ${color.line};
    flex-shrink: 0;
`;

const TitleInput = styled.input`
    flex-grow: 1;
    min-width: 0;
    padding: 6px 0;
    background: none;
    border: none;
    border-bottom: 1px solid transparent;
    border-radius: 0;
    color: ${color.textBright};
    font-family: ${font.display};
    font-weight: 800;
    font-size: 24px;
    letter-spacing: 0.08em;
    transition: border-color 0.2s ease;

    &::placeholder { color: ${color.text3}; }
    &:focus { outline: none; border-bottom-color: ${color.bloodHi}; }
`;

const CloseButton = styled.button`
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 1px solid ${color.line2};
    border-radius: 0;
    color: ${color.text2};
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease;
    &:hover { border-color: ${color.bloodHi}; color: ${color.text}; }
`;

const EditorContainer = styled.div`
    display: flex;
    flex-grow: 1;
    min-height: 0;

    @media (max-width: 760px) { flex-direction: column; }
`;

const Pane = styled.div`
    position: relative;
    width: 50%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    &:first-child { border-right: 1px solid ${color.line}; }
    .pane-label {
        flex-shrink: 0;
        padding: 8px 20px;
        border-bottom: 1px solid ${color.line};
        font-size: 12px;
        letter-spacing: 0.3em;
        color: ${color.text3};
    }

    @media (max-width: 760px) {
        width: 100%;
        flex: 1 1 50%;
        &:first-child { border-right: none; border-bottom: 1px solid ${color.line}; }
    }
`;

const Textarea = styled.textarea`
    flex-grow: 1;
    width: 100%;
    min-height: 0;
    padding: 20px;
    box-sizing: border-box;
    border: none;
    border-radius: 0;
    background-color: rgba(7, 2, 5, 0.6);
    color: ${color.text};
    font-size: 14px;
    font-family: ${font.mono};
    resize: none;
    line-height: 1.8;

    &::placeholder { color: ${color.text3}; font-family: ${font.body}; }
    &:focus { outline: none; box-shadow: inset 0 0 0 1px ${color.bloodHi}; }
`;

// Preview Styles (matching theme)
const Preview = styled.div`
    flex-grow: 1;
    min-height: 0;
    padding: 4px 24px 20px;
    box-sizing: border-box;
    overflow-y: auto;
    font-size: 15px;
    line-height: 1.9;
    color: ${color.text2};

    h1, h2, h3 {
        margin-top: 28px;
        padding-bottom: 8px;
        border-bottom: 1px solid ${color.line};
        font-family: ${font.display};
        letter-spacing: 0.08em;
        color: ${color.text};
    }
    p { margin: 14px 0; }
    strong { color: ${color.text}; }
    em { color: ${color.text2}; }
    a { color: ${color.bloodHi}; text-decoration: none; border-bottom: 1px solid rgba(224, 70, 79, 0.45); &:hover { color: ${color.textBright}; border-color: ${color.textBright}; } }
    code { font-family: ${font.mono}; font-size: 0.9em; background-color: ${color.surface2}; padding: 2px 5px; color: ${color.text}; }
    pre > code { display: block; padding: 14px 16px; overflow-x: auto; background-color: ${color.void}; border: 1px solid ${color.line}; }
    blockquote { margin-left: 0; padding-left: 16px; border-left: 2px solid ${color.blood}; color: ${color.text2}; }
    img { max-width: 100%; border: 1px solid ${color.line}; }
    ul, ol { padding-left: 22px; }
    hr { border: none; border-top: 1px solid ${color.line}; }
    table { border-collapse: collapse; }
    th, td { border: 1px solid ${color.line}; padding: 6px 10px; }
`;


const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    padding: 14px 24px;
    border-top: 1px solid ${color.line};
    flex-shrink: 0;
`;

const FooterLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    letter-spacing: 0.1em;
    color: ${color.text2};
    input { width: 16px; height: 16px; margin: 0; accent-color: ${color.blood}; cursor: pointer; }
    label { cursor: pointer; }
`;

const ErrorMessage = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${color.bloodHi};
`;


// --- 组件 ---
const NewPostEditor = ({ closeModal, onSuccess }) => {
    const { user } = useAuth();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [isOfficial, setIsOfficial] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isAdmin = user && ['site_owner', 'admin'].includes(user.role);

    const handleSubmit = async () => {
        if (!title.trim() || !content.trim()) {
            setError('标题和正文不能为空。');
            return;
        }
        setError('');
        setIsSubmitting(true);

        try {
            await apiClient.post('/user/forum/posts', {
                title,
                content,
                is_official: isAdmin && isOfficial,
            });
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.message || '发布失败，请稍后重试。');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalBackdrop>
            <ModalContent role="dialog" aria-modal="true" aria-label="发表新帖">
                <Header>
                    <TitleInput
                        type="text"
                        placeholder="输入标题"
                        aria-label="标题"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength="255"
                    />
                    <CloseButton type="button" onClick={closeModal} aria-label="关闭">×</CloseButton>
                </Header>

                <EditorContainer>
                    <Pane>
                        <div className="pane-label" id="editor-label">正文 · Markdown</div>
                        <Textarea
                            aria-labelledby="editor-label"
                            placeholder="使用 Markdown 语法书写正文…"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                        />
                    </Pane>
                    <Pane>
                        <div className="pane-label">预览</div>
                        <Preview>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {content || "*预览将显示在这里*"}
                            </ReactMarkdown>
                        </Preview>
                    </Pane>
                </EditorContainer>

                <Footer>
                    <FooterLeft>
                        {isAdmin && (
                            <CheckboxContainer>
                            <input
                                type="checkbox"
                                id="isOfficial"
                                checked={isOfficial}
                                onChange={(e) => setIsOfficial(e.target.checked)}
                            />
                            <label htmlFor="isOfficial">标记为官方帖</label>
                            </CheckboxContainer>
                        )}
                         {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
                    </FooterLeft>
                    <PrimaryButton type="button" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? '发布中…' : '发布'}
                    </PrimaryButton>
                </Footer>

            </ModalContent>
        </ModalBackdrop>
    );
};

export default NewPostEditor;